import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';

// --- SPACESHIP ASSETS ---
const RETROKID_SHIP = "https://customer-assets.emergentagent.com/job_02fb1ea7-05bc-49a3-acd9-1025e0802bad/artifacts/t2gheq52_Chibi%20robot%20piloting%20a%20flying%20saucer.png";
const DJKNOWAN_SHIP = "https://customer-assets.emergentagent.com/job_02fb1ea7-05bc-49a3-acd9-1025e0802bad/artifacts/5phav9r7_Chibi%20pilot%20in%20futuristic%20spacecraft.png";

// --- GO-KART ASSETS ---
const DJKNOWAN_KART = "https://customer-assets.emergentagent.com/job_02fb1ea7-05bc-49a3-acd9-1025e0802bad/artifacts/xyh11pba_DJ%20Knowan%20in%20futuristic%20go-kart.png";
const RETROKID_KART = "https://customer-assets.emergentagent.com/job_02fb1ea7-05bc-49a3-acd9-1025e0802bad/artifacts/eljd1gz4_Futuristic%20robot%20racing%20in%20sleek%20go-kart%281%29.png";

const shipAssets = [
  { src: RETROKID_SHIP, alt: 'Retro Kid Spaceship' },
  { src: DJKNOWAN_SHIP, alt: 'DJ KnoWan Spaceship' },
];

const kartAssets = [
  { src: DJKNOWAN_KART, alt: 'DJ KnoWan Go-Kart', facesRight: true },
  { src: RETROKID_KART, alt: 'Retro Kid Go-Kart', facesRight: false },
];

// Get the full viewport width for true full-screen traversal
const getVW = () => (typeof window !== 'undefined' ? window.innerWidth : 1920);

// ============================================================
// TOP: Spaceships — absolute inside hero, overflow-hidden.
// FULL WIDTH traversal using pixel values.
// ============================================================
export const HeroSpaceships = () => {
  const [spawn, setSpawn] = useState(() => ({
    key: 0,
    charIdx: 0,
    goRight: true,
    vertY: 25,
    startX: -300,
    endX: getVW() + 300,
  }));

  const spawnNext = useCallback(() => {
    const delay = 2000 + Math.random() * 3000;
    setTimeout(() => {
      const vw = getVW();
      const goRight = Math.random() > 0.5;
      setSpawn({
        key: Date.now(),
        charIdx: Math.floor(Math.random() * shipAssets.length),
        goRight,
        vertY: 10 + Math.random() * 40,
        startX: goRight ? -300 : vw + 300,
        endX: goRight ? vw + 300 : -300,
      });
    }, delay);
  }, []);

  const char = shipAssets[spawn.charIdx];
  const isMobile = getVW() < 768;
  const size = isMobile ? 100 : 150;
  const duration = 8 + Math.random() * 2;

  return (
    <div className="absolute inset-0 z-[2] pointer-events-none overflow-hidden" aria-hidden="true">
      <motion.div
        key={spawn.key}
        initial={{ x: spawn.startX }}
        animate={{ x: spawn.endX }}
        transition={{ duration, ease: [0.4, 0.0, 0.2, 1] }}
        onAnimationComplete={spawnNext}
        className="absolute"
        style={{ top: `${spawn.vertY}%` }}
      >
        {/* Separate floating layer — runs independently of horizontal travel */}
        <motion.div
          animate={{ y: [-12, 12, -12] }}
          transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
        >
          <img
            src={char.src}
            alt={char.alt}
            style={{
              width: size,
              height: 'auto',
              objectFit: 'contain',
              transform: spawn.goRight ? 'none' : 'scaleX(-1)',
              filter: 'drop-shadow(0 0 20px rgba(25,164,251,0.3))',
            }}
            draggable={false}
          />
        </motion.div>
      </motion.div>
    </div>
  );
};

// ============================================================
// BOTTOM: Go-karts — absolute in bottom container (z-55).
// FULL WIDTH traversal using pixel values.
// Direction always matches facing (scaleX flip if needed).
// ============================================================
export const BottomGoKarts = () => {
  const [spawn, setSpawn] = useState(null);

  const createSpawn = useCallback(() => {
    const vw = getVW();
    const goRight = Math.random() > 0.5;
    return {
      key: Date.now(),
      charIdx: Math.floor(Math.random() * kartAssets.length),
      goRight,
      startX: goRight ? -300 : vw + 300,
      endX: goRight ? vw + 300 : -300,
    };
  }, []);

  const spawnNext = useCallback(() => {
    const delay = 2000 + Math.random() * 3000;
    setTimeout(() => setSpawn(createSpawn()), delay);
  }, [createSpawn]);

  useEffect(() => {
    const t = setTimeout(() => setSpawn(createSpawn()), 4000);
    return () => clearTimeout(t);
  }, [createSpawn]);

  const onExit = useCallback(() => {
    setSpawn(null);
    spawnNext();
  }, [spawnNext]);

  if (!spawn) return null;

  const char = kartAssets[spawn.charIdx];
  const isMobile = getVW() < 768;
  const size = isMobile ? 100 : 150;
  const duration = 6 + Math.random() * 2;
  const needsFlip = spawn.goRight !== char.facesRight;

  return (
    <motion.div
      key={spawn.key}
      initial={{ x: spawn.startX }}
      animate={{ x: spawn.endX }}
      transition={{ duration, ease: "easeInOut" }}
      onAnimationComplete={onExit}
      className="absolute bottom-0"
      style={{ left: 0 }}
    >
      <motion.div
        animate={{ y: [0, -5, 0], rotate: [-1.5, 1.5, -1.5] }}
        transition={{ duration: 0.8, repeat: Infinity, ease: "easeInOut" }}
      >
        <img
          src={char.src}
          alt={char.alt}
          style={{
            width: size,
            height: 'auto',
            objectFit: 'contain',
            transform: needsFlip ? 'scaleX(-1)' : 'none',
            filter: 'drop-shadow(0 4px 15px rgba(0,0,0,0.5))',
          }}
          draggable={false}
        />
      </motion.div>
    </motion.div>
  );
};
